import React, {
  type CSSProperties,
  type Dispatch,
  type SetStateAction,
  useEffect,
  useState,
} from 'react'

import { useAuth } from '../../contexts/AuthContext'
import { Loading } from '../Loading'
import { DataParametersModal } from './DataParametersModal'
import { ImportDataProvider } from './ImportDataContext'
import {
  type BankTransaction,
  type BusinessIndustry,
  type CategorizationFlowSuggestion,
  type Category,
  type CustomAccount,
  getBusiness,
  getOrCreateDefaultCustomAccount,
  getOrCreateDefaultCustomDepositoryAccount,
  type SuggestedMatch,
  type SuggestedMatchType,
} from './importDummyDataHelpers'
import { ImportTilesWithDist } from './ImportTilesWithDist'

export const textAreaStyle: CSSProperties = {
  width: '100%',
  height: '200px',
  resize: 'none',
  overflowY: 'auto',
}

interface ImportDummyDataProps {
  demoName: string
  businessId: string
  businessAccessToken: string
  layerApiUrl: string
  setBookkeepingMode: (value: boolean) => void
  setImportingTransactions: Dispatch<SetStateAction<boolean>>
}

type BookkeepingPeriodStatus =
  | 'BOOKKEEPING_NOT_ACTIVE'
  | 'NOT_STARTED'
  | 'IN_PROGRESS_AWAITING_BOOKKEEPER'
  | 'IN_PROGRESS_AWAITING_CUSTOMER'
  | 'CLOSING_IN_REVIEW'
  | 'CLOSED_OPEN_TASKS'
  | 'CLOSED_COMPLETE'

type BookkeepingPeriod = {
  id: string
  month: number | null
  year: number | null
  scale: 'MONTHLY' | 'ANNUAL' | 'ONGOING'
  status: BookkeepingPeriodStatus
}

type BookkeepingPeriodsResponse = {
  periods?: BookkeepingPeriod[]
  data?: {
    periods?: BookkeepingPeriod[]
  }
}

type LayerBankTransaction = {
  id: string
  date: string
  amount: number
  counterparty_name: string | null
  description: string | null
  categorization_status: string
  suggested_matches: unknown[]
}

type LayerBankTransactionsResponse = {
  data: LayerBankTransaction[]
}

type AccountingConfigurationResponse = {
  enable_tax_estimates?: boolean
  enable_mileage_tracking?: boolean
}

type YearMonth = {
  year: number
  month: number
}

const EASTERN_TIME_ZONE = 'America/New_York'
const uncategorizedStatuses = new Set([
  'PENDING',
  'LAYER_REVIEW',
  'READY_FOR_INPUT',
])

const easternMonthDayFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: EASTERN_TIME_ZONE,
  month: 'long',
  day: 'numeric',
})

const usdFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
})

const getErrorMessage = async (response: Response) => {
  const responseText = await response.text()

  if (!responseText) {
    return `${response.status} ${response.statusText}`
  }

  try {
    const parsed = JSON.parse(responseText)
    if (typeof parsed?.message === 'string') {
      return parsed.message
    }
  } catch (_error) {
    // Fall through to the raw response text below.
  }

  return responseText
}

const ensureSuccess = async (response: Response, action: string) => {
  if (!response.ok) {
    throw new Error(`${action}: ${await getErrorMessage(response)}`)
  }
}

const formatDateParam = ({ year, month }: YearMonth): string =>
  `${year}-${String(month).padStart(2, '0')}-01`

const getNextMonth = ({ year, month }: YearMonth): YearMonth =>
  month === 12 ? { year: year + 1, month: 1 } : { year, month: month + 1 }

const isDateOnlyString = (value: string) => /^\d{4}-\d{2}-\d{2}$/.test(value)

const formatQuestionDate = (isoDate: string): string => {
  if (isDateOnlyString(isoDate)) {
    const [year, month, day] = isoDate.split('-').map(Number)
    return easternMonthDayFormatter.format(
      new Date(Date.UTC(year, month - 1, day, 12)),
    )
  }

  return easternMonthDayFormatter.format(new Date(isoDate))
}

const formatTransactionAmount = (amountInCents: number): string =>
  usdFormatter.format(amountInCents / 100)

const getTransactionMerchantLabel = (
  transaction: Pick<LayerBankTransaction, 'counterparty_name' | 'description'>,
): string =>
  transaction.counterparty_name?.trim()
  || transaction.description?.trim()
  || 'this merchant'

const getTransactionTaskTitle = (transaction: LayerBankTransaction): string =>
  `Purchase at ${getTransactionMerchantLabel(transaction)}`

const getTransactionTaskQuestion = (transaction: LayerBankTransaction): string =>
  `On ${formatQuestionDate(transaction.date)} you spent ${formatTransactionAmount(transaction.amount)} at ${getTransactionMerchantLabel(transaction)}. Can you share what this purchase was for?`


export const ImportDummyData = ({
  demoName,
  businessId,
  businessAccessToken,
  layerApiUrl,
  setBookkeepingMode,
  setImportingTransactions,
}: ImportDummyDataProps) => {
  const { authFetch } = useAuth()
  const [loading, setLoading] = useState(true)
  const [showDataParams, setShowDataParams] = useState(false)
  const [defaultCustomAccount, setDefaultCustomAccount] =
    useState<CustomAccount | null>(null)
  const [defaultCustomDepositoryAccount, setDefaultCustomDepositoryAccount] =
    useState<CustomAccount | null>(null)

  const [industry, setIndustry] = useState<BusinessIndustry | null>(null)
  const [activeAction, setActiveAction] = useState<
    'activate-bookkeeping' | 'deactivate-bookkeeping' | 'create-tasks' | null
  >(null)
  const [enableTaxEstimates, setEnableTaxEstimates] = useState(false)
  const [enableMileageTracking, setEnableMileageTracking] = useState(false)
  const [activationAt, setActivationAt] = useState<string | null>(null)
  const [activeBookkeepingOption, setActiveBookkeepingOption] = useState<
    'tax-estimates' | 'mileage-tracking' | null
  >(null)
  const isBookkeepingActionDisabled = activeAction !== null

  useEffect(() => {
    const initializeAccounts = async () => {
      try {
        const [creditCardAccount, depositoryAccount] = await Promise.all([
          getOrCreateDefaultCustomAccount(businessId, businessAccessToken, layerApiUrl),
          getOrCreateDefaultCustomDepositoryAccount(businessId, businessAccessToken, layerApiUrl),
        ])

        setDefaultCustomAccount(creditCardAccount)
        setDefaultCustomDepositoryAccount(depositoryAccount)
        const business = await getBusiness(businessId, businessAccessToken, layerApiUrl)
        setIndustry(business.industry)
        setActivationAt(business.activation_at ?? business.importedAt)
        const accountingConfigResponse = await fetch(
          `${layerApiUrl}/v1/businesses/${businessId}/accounting-config`,
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${businessAccessToken}`,
              'Content-Type': 'application/json',
            },
          },
        )
        await ensureSuccess(
          accountingConfigResponse,
          'Failed to load accounting configuration',
        )

        const accountingConfig =
          (await accountingConfigResponse.json()) as AccountingConfigurationResponse
        setEnableTaxEstimates(accountingConfig.enable_tax_estimates ?? false)
        setEnableMileageTracking(
          accountingConfig.enable_mileage_tracking ?? false,
        )

        setLoading(false)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error initializing accounts:', error)
        setLoading(false)
      }
    }

    initializeAccounts()
  }, [businessId, businessAccessToken, layerApiUrl])

  const updateDemoBookkeepingMode = async (bookkeepingOn: boolean) => {
    const updateResponse = await authFetch(`/api/demos/${demoName}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        clientName: demoName,
        bookkeepingOn,
      }),
    })

    await ensureSuccess(updateResponse, 'Failed to persist demo bookkeeping mode')
  }

  const updateBookkeepingConfigurationOptions = async (
    options: {
      enableTaxEstimates: boolean
      enableMileageTracking: boolean
    },
  ) => {
    const updateResponse = await fetch(
      `${layerApiUrl}/v1/businesses/${businessId}/accounting-config`,
      {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${businessAccessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          enable_tax_estimates: options.enableTaxEstimates,
          enable_mileage_tracking: options.enableMileageTracking,
        }),
      },
    )

    await ensureSuccess(
      updateResponse,
      'Failed to update accounting configuration',
    )
  }

  const getMonthlyBookkeepingPeriods = async () => {
    const periodsResponse = await fetch(
      `${layerApiUrl}/v1/businesses/${businessId}/bookkeeping/periods?scale=MONTHLY`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${businessAccessToken}`,
          'Content-Type': 'application/json',
        },
      },
    )
    await ensureSuccess(periodsResponse, 'Failed to fetch bookkeeping periods')

    const periodsData =
      (await periodsResponse.json()) as BookkeepingPeriodsResponse
    const periods =
      periodsData.periods
      ?? periodsData.data?.periods
      ?? (Array.isArray(periodsData) ? periodsData : null)

    if (!periods) {
      throw new Error(
        `Failed to fetch bookkeeping periods: unexpected response ${JSON.stringify(periodsData)}`,
      )
    }

    return periods.filter(
      period =>
        period.scale === 'MONTHLY' && period.month !== null && period.year !== null,
    )
  }

  const getTransactionsForPeriod = async (period: BookkeepingPeriod) => {
    const yearMonth = {
      year: period.year ?? 0,
      month: period.month ?? 0,
    }
    const nextYearMonth = getNextMonth(yearMonth)
    const transactionsResponse = await fetch(
      `${layerApiUrl}/v1/businesses/${businessId}/bank-transactions?start_date=${formatDateParam(yearMonth)}&end_date=${formatDateParam(nextYearMonth)}&direction=MONEY_OUT&categorized=false&limit=500`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${businessAccessToken}`,
          'Content-Type': 'application/json',
        },
      },
    )

    await ensureSuccess(
      transactionsResponse,
      `Failed to fetch transactions for ${period.year}-${period.month}`,
    )

    const transactionsData =
      (await transactionsResponse.json()) as LayerBankTransactionsResponse

    return transactionsData.data.filter(
      transaction =>
        uncategorizedStatuses.has(transaction.categorization_status)
        && transaction.suggested_matches.length === 0,
    )
  }

  const createTaskForTransaction = async (transaction: LayerBankTransaction) => {
    const createTaskResponse = await fetch(
      `${layerApiUrl}/v1/businesses/${businessId}/tasks`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${businessAccessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_response_type: 'FREE_RESPONSE',
          transaction_id: transaction.id,
          title: getTransactionTaskTitle(transaction),
          question: getTransactionTaskQuestion(transaction),
        }),
      },
    )

    await ensureSuccess(
      createTaskResponse,
      `Failed to create task for transaction ${transaction.id}`,
    )
  }

  const OtherActions = () => {
    const ApproveMatchesButton = () => {
      const handleSubmit = async () => {
        const today = new Date()
        const firstDayOfMonth = new Date(
          today.getFullYear(),
          today.getMonth(),
          1,
        )
        const sevenDaysAgo = new Date(today)
        sevenDaysAgo.setDate(today.getDate() - 7)
        const categorizationCutoffDate = (
          firstDayOfMonth < sevenDaysAgo ? firstDayOfMonth : sevenDaysAgo
        ).toISOString()

        try {
          const txnsLayerReviewResponse = await fetch(
            `${layerApiUrl}/v1/businesses/${businessId}/bank-transactions?categorization_status=LAYER_REVIEW&end_date=${categorizationCutoffDate}&limit=500`,
            {
              method: 'GET',
              headers: {
          Authorization: `Bearer ${businessAccessToken}`,
                'Content-Type': 'application/json',
              },
            },
          )
          const txnsLayerReviewData = await txnsLayerReviewResponse.json()
          const txnsReadyForInputResponse = await fetch(
            `${layerApiUrl}/v1/businesses/${businessId}/bank-transactions?categorization_status=READY_FOR_INPUT&end_date=${categorizationCutoffDate}&limit=500`,
            {
              method: 'GET',
              headers: {
          Authorization: `Bearer ${businessAccessToken}`,
                'Content-Type': 'application/json',
              },
            },
          )
          const txnsReadyForInputData = await txnsReadyForInputResponse.json()

          // Helper function to get valid match type based on source_transaction_id
          const getValidMatchTypeForTransaction = (
            txn: BankTransaction,
          ): SuggestedMatchType | null => {
            const sourceId = txn.source_transaction_id

            // Filter out transactions with -transfer-debit-
            if (sourceId.includes('-transfer-debit-')) {
              return null // Skip these transactions
            }

            // Match the source_transaction_id pattern to the required match type
            if (sourceId.includes('-transfer-debit-')) {
              return 'TRANSFER'
            } else if (sourceId.includes('-payout-match-')) {
              return 'PAYOUT'
            } else if (sourceId.includes('manual-entry-match-')) {
              return 'MANUAL_JOURNAL_ENTRY'
            } else if (sourceId.includes('-custom-match-')) {
              return 'INVOICE_PAYMENT'
            } else if (sourceId.includes('-bill-payment-')) {
              return 'BILL_PAYMENT'
            }

            // Default case - no specific match required
            return null
          }

          // Set to track unique match detail IDs
          const usedMatchDetailIds = new Set<string>()

          // Process the Layer Review transactions
          const transactionLayerReviewMatchPairs = (
            txnsLayerReviewData.data as BankTransaction[]
          )
            .filter(txn => {
              // Skip transactions with transfer-credit
              if (txn.source_transaction_id.includes('-transfer-debit-')) {
                return false
              }

              // Skip transactions with custom-expense
              if (txn.source_transaction_id.includes('custom-expense')) {
                return false
              }

              // Get the valid match type for this transaction
              const validMatchType = getValidMatchTypeForTransaction(txn)

              // Filter for matches that:
              // 1. Have the correct match type (if specified)
              // 2. Have no adjustment
              // 3. Have dates that exactly match the transaction date
              return txn.suggested_matches.some(match => {
                const hasCorrectType =
                  validMatchType === null || match.matchType === validMatchType
                const hasNoAdjustment = match.details.adjustment === null
                const datesMatch = match.details.date === txn.date

                return hasCorrectType && hasNoAdjustment && datesMatch
              })
            })
            .map(txn => {
              const validMatchType = getValidMatchTypeForTransaction(txn)

              // Find the first valid match
              const suggestedMatch = txn.suggested_matches.find(match => {
                const hasCorrectType =
                  validMatchType === null || match.matchType === validMatchType
                const hasNoAdjustment = match.details.adjustment === null
                const datesMatch = match.details.date === txn.date
                // Check if this match detail ID has already been used
                const isUnique = !usedMatchDetailIds.has(match.details.id)

                return (
                  hasCorrectType && hasNoAdjustment && datesMatch && isUnique
                )
              })

              // If we found a valid match, add its ID to our set to prevent duplicates
              if (suggestedMatch) {
                usedMatchDetailIds.add(suggestedMatch.details.id)
                return {
                  transaction_id: txn.id,
                  suggested_match_id: suggestedMatch.id,
                }
              }

              // Return null if no valid match is found - we'll filter these out below
              return null
            })
            .filter(
              (
                pair,
              ): pair is {
                transaction_id: string
                suggested_match_id: string
              } => pair !== null,
            )

          // Reset the set for the Ready for Input transactions
          usedMatchDetailIds.clear()

          // Process the Ready for Input transactions (similar logic)
          const transactionReadyForInputMatchPairs = (
            txnsReadyForInputData.data as BankTransaction[]
          )
            .filter(txn => {
              // Skip transactions with transfer-credit
              if (txn.source_transaction_id.includes('-transfer-debit-')) {
                return false
              }

              // Skip transactions with custom-expense
              if (txn.source_transaction_id.includes('custom-expense')) {
                return false
              }

              // Get the valid match type for this transaction
              const validMatchType = getValidMatchTypeForTransaction(txn)

              // Filter for matches that meet our criteria
              return txn.suggested_matches.some(match => {
                const hasCorrectType =
                  validMatchType === null || match.matchType === validMatchType
                const hasNoAdjustment = match.details.adjustment === null
                const datesMatch = match.details.date === txn.date

                return hasCorrectType && hasNoAdjustment && datesMatch
              })
            })
            .map(txn => {
              const validMatchType = getValidMatchTypeForTransaction(txn)

              // Find the first valid match
              const suggestedMatch = txn.suggested_matches.find(match => {
                const hasCorrectType =
                  validMatchType === null || match.matchType === validMatchType
                const hasNoAdjustment = match.details.adjustment === null
                const datesMatch = match.details.date === txn.date
                const isUnique = !usedMatchDetailIds.has(match.details.id)

                return (
                  hasCorrectType && hasNoAdjustment && datesMatch && isUnique
                )
              })

              // If we found a valid match, add its ID to our set
              if (suggestedMatch) {
                usedMatchDetailIds.add(suggestedMatch.details.id)
                return {
                  transaction_id: txn.id,
                  suggested_match_id: suggestedMatch.id,
                }
              }

              return null
            })
            .filter(
              (
                pair,
              ): pair is {
                transaction_id: string
                suggested_match_id: string
              } => pair !== null,
            )

          const bulkMatchLayerReviewResponse = await fetch(
            `${layerApiUrl}/v1/businesses/${businessId}/bank-transactions/bulk-match`,
            {
              method: 'POST',
              headers: {
          Authorization: `Bearer ${businessAccessToken}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                match_pairs: transactionLayerReviewMatchPairs,
              }),
            },
          )

          const bulkMatchReadyForInputResponse = await fetch(
            `${layerApiUrl}/v1/businesses/${businessId}/bank-transactions/bulk-match`,
            {
              method: 'POST',
              headers: {
          Authorization: `Bearer ${businessAccessToken}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                match_pairs: transactionReadyForInputMatchPairs,
              }),
            },
          )
          bulkMatchLayerReviewResponse.json()
          bulkMatchReadyForInputResponse.json()

          alert('Transaction matches matched approved successfully!')
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error approving transactions:', error)
          alert(`Failed to approve transactions: ${error}`)
        }
      }

      return (
        <button className='admin-button' onClick={handleSubmit}>
          Approve all matched transactions
        </button>
      )
    }
    const CategorizeTransactionsButton = () => {
      const handleSubmit = async () => {
        const today = new Date()
        const firstDayOfMonth = new Date(
          today.getFullYear(),
          today.getMonth(),
          1,
        )
        const sevenDaysAgo = new Date(today)
        sevenDaysAgo.setDate(today.getDate() - 7)
        const categorizationCutoffDate = (
          firstDayOfMonth < sevenDaysAgo ? firstDayOfMonth : sevenDaysAgo
        ).toISOString()

        try {
          const txnsReadyForInputResponse = await fetch(
            `${layerApiUrl}/v1/businesses/${businessId}/bank-transactions?categorization_status=READY_FOR_INPUT&end_date=${categorizationCutoffDate}&limit=500`,
            {
              method: 'GET',
              headers: {
                Authorization: `Bearer ${businessAccessToken}`,
                'Content-Type': 'application/json',
              },
            },
          )
          const txnsReadyForInputData = await txnsReadyForInputResponse.json()
          const transactionReadyForInputCategorizationPairs: {
            transaction_id: string
            category: CategorizationFlowSuggestion
          }[] = txnsReadyForInputData.data
            .filter(
              (txn: BankTransaction) =>
                txn.suggested_matches.filter(
                  (suggestedMatch: SuggestedMatch) =>
                    suggestedMatch.details.adjustment === null,
                ).length === 0 && txn.categorization_flow.suggestions.length > 0,
            )
            .map((txn: BankTransaction) => ({
              transaction_id: txn.id,
              category: txn.categorization_flow.suggestions[0],
            }))

          const delay = (ms: number) =>
            new Promise(resolve => setTimeout(resolve, ms))
          for (const element of transactionReadyForInputCategorizationPairs) {
            await delay(50)
            const category: Category = {
              type: 'Category',
              category: {
                type: 'StableName',
                stable_name: element.category.category,
              },
            }
            const bulkMatchReadyForInputResponse = await fetch(
              `${layerApiUrl}/v1/businesses/${businessId}/bank-transactions/${element.transaction_id}/categorize`,
              {
                method: 'PUT',
                headers: {
                  Authorization: `Bearer ${businessAccessToken}`,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify(category),
              },
            )
            bulkMatchReadyForInputResponse.json()
          }

          alert('Transactions categorized successfully!')
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error categorizing transactions:', error)
          alert(`Failed to categorize transactions: ${error}`)
        }
      }

      return (
        <button className='admin-button' onClick={handleSubmit}>
          Categorize all transactions with first suggestion
        </button>
      )
    }

    const SyncBusinessButton = () => {
      const handleSubmit = () => {
        try {
          const _syncResponse = fetch(
            `${layerApiUrl}/v1/businesses/${businessId}/sync?synchronous=true`,
            {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${businessAccessToken}`,
                'Content-Type': 'application/json',
              },
            },
          )
          alert(
            'Business sync initiated. The initial backfill will take ~90 seconds.',
          )
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error syncing business:', error)
          alert(`Failed to sync business: ${error}`)
        }
      }
      return (
        <button className='admin-button' onClick={handleSubmit}>
          Sync Business
        </button>
      )
    }

    const ActivateBookkeepingButton = () => {
      const isLoading = activeAction === 'activate-bookkeeping'
      const isTaxEstimatesLoading = activeBookkeepingOption === 'tax-estimates'
      const isMileageTrackingLoading =
        activeBookkeepingOption === 'mileage-tracking'

      const handleOptionChange = async (
        option: 'tax-estimates' | 'mileage-tracking',
        checked: boolean,
      ) => {
        if (activeBookkeepingOption !== null || isBookkeepingActionDisabled) {
          return
        }

        const previousTaxEstimates = enableTaxEstimates
        const previousMileageTracking = enableMileageTracking
        const nextOptions = {
          enableTaxEstimates:
            option === 'tax-estimates' ? checked : enableTaxEstimates,
          enableMileageTracking:
            option === 'mileage-tracking' ? checked : enableMileageTracking,
        }

        setActiveBookkeepingOption(option)
        setEnableTaxEstimates(nextOptions.enableTaxEstimates)
        setEnableMileageTracking(nextOptions.enableMileageTracking)

        try {
          await updateBookkeepingConfigurationOptions(nextOptions)
        } catch (error) {
          setEnableTaxEstimates(previousTaxEstimates)
          setEnableMileageTracking(previousMileageTracking)
          // eslint-disable-next-line no-console
          console.error('Error updating bookkeeping option:', error)
          alert(`Failed to update bookkeeping option: ${error}`)
        } finally {
          setActiveBookkeepingOption(null)
        }
      }

      const handleSubmit = async () => {
        if (isBookkeepingActionDisabled || activeBookkeepingOption !== null) {
          return
        }

        setActiveAction('activate-bookkeeping')

        try {
          const activateResponse = await authFetch(
            `/api/demos/${demoName}/bookkeeping/activate`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                businessId,
                businessAccessToken,
                enableTaxEstimates,
                enableMileageTracking,
              }),
            },
          )
          await ensureSuccess(
            activateResponse,
            'Failed to activate bookkeeping',
          )

          setBookkeepingMode(true)
          alert('Bookkeeping activated successfully!')
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error activating bookkeeping:', error)
          alert(`Failed to activate bookkeeping: ${error}`)
        } finally {
          setActiveAction(null)
        }
      }

      return (
        <>
          <label>
            <input
              type='checkbox'
              checked={enableTaxEstimates}
              disabled={
                isBookkeepingActionDisabled || activeBookkeepingOption !== null
              }
              onChange={event =>
                void handleOptionChange('tax-estimates', event.target.checked)}
            />
            {isTaxEstimatesLoading
              ? 'Updating tax estimates...'
              : 'Enable tax estimates'}
          </label>
          <label>
            <input
              type='checkbox'
              checked={enableMileageTracking}
              disabled={
                isBookkeepingActionDisabled || activeBookkeepingOption !== null
              }
              onChange={event =>
                void handleOptionChange(
                  'mileage-tracking',
                  event.target.checked,
                )}
            />
            {isMileageTrackingLoading
              ? 'Updating mileage tracking...'
              : 'Enable mileage tracking'}
          </label>
          <button
            className='admin-button'
            onClick={handleSubmit}
            disabled={
              isBookkeepingActionDisabled || activeBookkeepingOption !== null
            }
          >
            {isLoading ? 'Activating bookkeeping...' : 'Activate bookkeeping'}
          </button>
        </>
      )
    }

    const DeactivateBookkeepingButton = () => {
      const isLoading = activeAction === 'deactivate-bookkeeping'

      const handleSubmit = async () => {
        if (isBookkeepingActionDisabled) {
          return
        }

        setActiveAction('deactivate-bookkeeping')

        try {
          const deactivateResponse = await fetch(
            `${layerApiUrl}/v1/businesses/${businessId}/bookkeeping/deactivate`,
            {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${businessAccessToken}`,
                'Content-Type': 'application/json',
              },
            },
          )
          await ensureSuccess(
            deactivateResponse,
            'Failed to deactivate bookkeeping',
          )

          await updateDemoBookkeepingMode(false)
          setBookkeepingMode(false)
          alert('Bookkeeping deactivated successfully!')
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error deactivating bookkeeping:', error)
          alert(`Failed to deactivate bookkeeping: ${error}`)
        } finally {
          setActiveAction(null)
        }
      }

      return (
        <button
          className='admin-button'
          onClick={handleSubmit}
          disabled={isBookkeepingActionDisabled}
        >
          {isLoading
            ? 'Deactivating bookkeeping...'
            : 'Deactivate bookkeeping'}
        </button>
      )
    }

    const CreateTasksButton = () => {
      const isLoading = activeAction === 'create-tasks'

      const handleSubmit = async () => {
        if (isBookkeepingActionDisabled) {
          return
        }

        setActiveAction('create-tasks')

        try {
          const periods = await getMonthlyBookkeepingPeriods()
          const eligiblePeriods = periods.filter(
            period =>
              period.status !== 'CLOSED_COMPLETE'
              && period.status !== 'BOOKKEEPING_NOT_ACTIVE',
          )

          let tasksCreatedCount = 0

          for (const period of eligiblePeriods) {
            const transactions = await getTransactionsForPeriod(period)
            const transactionsForTasks = transactions.slice(0, 3)

            for (const transaction of transactionsForTasks) {
              await createTaskForTransaction(transaction)
              tasksCreatedCount += 1
            }
          }

          if (tasksCreatedCount === 0) {
            alert('No eligible transactions found, so no tasks were created.')
            return
          }

          alert(`Created ${tasksCreatedCount} bookkeeping task(s)!`)
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error creating bookkeeping tasks:', error)
          alert(`Failed to create bookkeeping tasks: ${error}`)
        } finally {
          setActiveAction(null)
        }
      }

      return (
        <button
          className='admin-button'
          onClick={handleSubmit}
          disabled={isBookkeepingActionDisabled}
        >
          {isLoading ? 'Creating tasks...' : 'Create tasks'}
        </button>
      )
    }

    return (
      <fieldset
        style={{
          padding: '20px',
          border: '2px solid black',
          borderRadius: '5px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '10px',
          marginTop: '20px',
        }}
      >
        <legend style={{ fontSize: '1.2em', padding: '0 10px' }}>
          Other Actions
        </legend>
        <ApproveMatchesButton />
        <CategorizeTransactionsButton />
        <SyncBusinessButton />
        <ActivateBookkeepingButton />
        <DeactivateBookkeepingButton />
        <CreateTasksButton />
      </fieldset>
    )
  }

  if (loading) return <Loading />

  return (
    <ImportDataProvider layerApiUrl={layerApiUrl}>
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        minHeight: '100vh',
        width: '100%',
        position: 'relative',
        overflowY: 'auto',
        boxSizing: 'border-box',
        padding: '0 0 32px',
      }}
    >
      {showDataParams && <DataParametersModal onClose={() => setShowDataParams(false)} />}
      <h1>Import Transaction Data</h1>
      <button
        onClick={() => setShowDataParams(true)}
        style={{ marginBottom: 16 }}
      >
        Configure Revenue, Opex, and Bills parameters
      </button>
      {defaultCustomAccount === null
      || defaultCustomDepositoryAccount === null ? (
        <>
          No Custom Account found for business {businessId}
          <button
            onClick={async () => {
              const [creditCardAccount, depositoryAccount] = await Promise.all([
                getOrCreateDefaultCustomAccount(businessId, businessAccessToken, layerApiUrl),
                getOrCreateDefaultCustomDepositoryAccount(
                  businessId,
                  businessAccessToken,
                  layerApiUrl,
                ),
              ])
              setDefaultCustomAccount(creditCardAccount)
              setDefaultCustomDepositoryAccount(depositoryAccount)
            }}
          >
            Get or create default accounts
          </button>
        </>
      ) : industry === null || activationAt === null ? (
        <>Business details unavailable for business {businessId}</>
      ) : (
        <ImportTilesWithDist
          demoName={demoName}
          businessId={businessId}
          industry={industry}
          businessAccessToken={businessAccessToken}
          defaultCustomAccount={defaultCustomAccount}
          defaultCustomDepositoryAccount={defaultCustomDepositoryAccount}
          activationAt={activationAt}
        />
      )}
      <br />
      <OtherActions />
      <button
        className='admin-button'
        style={{
          marginTop: '20px',
          width: '120px',
          marginBottom: '20px',
          left: '20px',
        }}
        onClick={() => setImportingTransactions(false)}
      >
        Return to demo
      </button>
    </div>
    </ImportDataProvider>
  )
}
